import { PaginationMetadata } from "./pagination"
import { HouseholdRole } from "./person"
import { TaskAttachment } from "./task"

export enum InsuranceType {
  Life = "life",
  Disability = "disability",
  LongTermCare = "ltc",
  Health = "health",
  PropertyCasualty = "property_casualty",
  Umbrella = "umbrella",
}

export enum LifePolicyType {
  Term = "term",
  Permanent = "permanent",
}

export enum PermanentPolicyType {
  WholeLife = "whole_life",
  UniversalLife = "universal_life",
  IndexedUniversalLife = "indexed_universal_life",
  VariableUniversalLife = "variable_universal_life",
}

export enum PremiumFrequency {
  Monthly = "monthly",
  Quarterly = "quarterly",
  Semiannual = "semiannual",
  Annual = "annual",
  Single = "single",
  Other = "other",
}

export interface InsurancePolicyParty {
  id: string
  full_name: string | null
  preferred_name: string | null
  household_role: HouseholdRole
}

export const INSURANCE_POLICY_BENEFICIARY_TYPES = ["Person", "Trust", "Household", "Business"] as const

export type InsurancePolicyBeneficiaryType = (typeof INSURANCE_POLICY_BENEFICIARY_TYPES)[number]

export interface InsurancePolicyBeneficiary {
  id: string
  type: InsurancePolicyBeneficiaryType
  name: string | null
}

export interface InsurancePolicy {
  id: string
  household_id: string
  household: {
    id: string
    name: string
    type: "Client" | "Prospect" | "PastClient"
  }
  name: string | null
  insurance_type: InsuranceType
  carrier?: string | null
  policy_number?: string | null
  integration_backed: boolean
  life_policy_type?: LifePolicyType | null
  permanent_policy_type?: PermanentPolicyType | null
  term_duration_years?: number | null
  convertible?: boolean | null
  face_amount?: number | null
  death_benefit?: number | null
  coverage_amount?: number | null
  cash_value?: number | null
  policy_date?: string | null
  premium?: number | null
  premium_frequency?: PremiumFrequency | null
  premium_due_date?: string | null
  current_policy_year?: number | null
  property_casualty_type?: string | null
  effective_date?: string | null
  renewal_date?: string | null
  deductibles: string[]
  agent_broker?: string | null
  notes?: string | null
  owner_ids: string[]
  owners: InsurancePolicyParty[]
  insured_ids: string[]
  insureds: InsurancePolicyParty[]
  named_insured_ids: string[]
  named_insureds: InsurancePolicyParty[]
  additional_insured_ids: string[]
  additional_insureds: InsurancePolicyParty[]
  beneficiary_id: string | null
  beneficiary_type: InsurancePolicyBeneficiaryType | null
  beneficiary: InsurancePolicyBeneficiary | null
  attachments: TaskAttachment[]
  created_at: string
  updated_at: string
}

export type InsurancePolicyInput = Omit<
  InsurancePolicy,
  | "id"
  | "household_id"
  | "household"
  | "owners"
  | "insureds"
  | "named_insureds"
  | "additional_insureds"
  | "beneficiary"
  | "created_at"
  | "updated_at"
  | "coverage_amount"
  | "integration_backed"
  | "attachments"
> & {
  custom_fields_attributes?: Record<string, unknown>
  files?: string[]
}

export interface InsurancePoliciesResponse {
  insurance_policies: InsurancePolicy[]
  pagination?: PaginationMetadata
}

export interface InsurancePolicyExtractionResponse {
  insurance_policy: Partial<InsurancePolicyInput>
  unmatched_people: string[]
}
